/**
 * Build a NEW task doc + its notification docs — the logic ToDo.vue::saveTodo carried
 * inline until 2026-09-02, now shared by My Tasks and the Patient Profile panel.
 * Golden tests: cypress/node-tests/task-draft.test.js (executes this file).
 */
import moment from 'moment-timezone';
import { getAllLinkedEmployeeIDs } from './taskMentions';
import { getTaggedPatientIDs, hasTaskText } from './taskPatients';

export const DEFAULT_TIMEZONE = 'America/New_York';
export const EMPTY_TASK_MESSAGE = 'Please enter a task description before submitting';

export function dateFormatFor(timezone) {
  return (timezone || DEFAULT_TIMEZONE).toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
}

/** null when OK, else the toast message. */
export function validateDraft(contentHTML) {
  return hasTaskText(contentHTML) ? null : EMPTY_TASK_MESSAGE;
}

/**
 * Ported VERBATIM from ToDo.vue::removedAssignedTo — do not "fix" the regex.
 *
 * IMPORTANT: this pattern expects the inner span as `class="badge ... badge-primary"`
 * FIRST, then `data-teamio-employeeid=`. The REAL <at> picker emits the attributes in
 * the opposite order (tabindex → data-teamio-employeeid → class), so on real assignee
 * markup this regex NEVER matches — the employee badge stays in task.content. That is
 * the PRE-EXISTING saveTodo behaviour, not a new bug; the regex only strips the LEGACY
 * class-first order it was originally written for. Do NOT reorder the attributes or
 * otherwise "fix" the pattern without a deliberate, tested decision to change
 * task.content for EVERY future task.
 */
export function removeAssigneeBadges(contentHTML) {
  // eslint-disable-next-line no-useless-escape
  const regex = /<span data-at-embedded="" contenteditable="false"> <span class="badge mr-xs badge-primary.*data-teamio-employeeid="([a-zA-z0-9\-]+).*?<\/span>\s+<\/span>/;
  return (contentHTML || '').replace(new RegExp(regex, 'gm'), '');
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isProperDate(v) {
  return v instanceof Date
    || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v))
    || (typeof v === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}/.test(v));
}

export function buildNewTask({
  contentHTML, siteID, userID, userName, timezone, maxSortID,
  selectedTags = [], detectedDateInfo = null, selectedDueDate = null, now = new Date(),
}) {
  // A shared entry point must never write a task with no owner (SmileBAR undefined-field
  // incident — see cypress/node-tests/task-undefined-field-hardening.test.js). Fail loud.
  if (!siteID || !userID) throw new Error('buildNewTask: siteID and userID are required');
  const officeTimezone = timezone || DEFAULT_TIMEZONE;
  const dateFormat = dateFormatFor(officeTimezone);

  const assignees = getAllLinkedEmployeeIDs(contentHTML);
  let assignedToEmpID = assignees.length ? assignees[0] : null;
  const content = removeAssigneeBadges(contentHTML);

  assignedToEmpID = assignedToEmpID ? assignedToEmpID : userID;
  if (assignees.length === 0) assignees.push(assignedToEmpID);

  const task = {
    siteID,
    status: 'INCOMPLETE',
    tags: [...(selectedTags || [])],
    sortID: maxSortID ? maxSortID + 1 : 1,
    content,
    createdAt: now,
    dueAt: now,
    dueAtStr: moment(now).format('MM/DD/YYYY'),
    assignedToEmpID,
    assignees,
    createdByEmpID: userID,
    timestamp: now.getTime(),
    timezone: officeTimezone,
    lastActivity: now,
    deleted: false,
  };

  // ── due date + recurrence: same branches, same order, as saveTodo ──
  if (detectedDateInfo && detectedDateInfo.hasDate) {
    if (detectedDateInfo.date) {
      if (isProperDate(detectedDateInfo.date)) {
        const normalized = moment.tz(detectedDateInfo.date, officeTimezone).startOf('day');
        task.dueAt = normalized.toDate();
        task.dueAtStr = normalized.format(dateFormat);
      } else {
        const startOfToday = moment.tz(now, officeTimezone).startOf('day');
        task.dueAt = startOfToday.toDate();
        task.dueAtStr = startOfToday.format(dateFormat);
      }
    }
    if (detectedDateInfo.isRecurring) {
      task.repeat = true;
      task.repeatFrequency = detectedDateInfo.frequency;
      const dow = detectedDateInfo.daysOfWeek;
      if ((task.repeatFrequency === 'Weekly' || task.repeatFrequency === 'Bi-Weekly') && dow && dow.length > 0) {
        task.repeatOn = DAY_NAMES[dow[0]];
      }
      if (task.repeatFrequency === 'Monthly') {
        if (detectedDateInfo.isLastDayOfMonth) {
          task.repeatOnLastDayOfMonth = true;
          delete task.repeatOnDay;
        } else if (detectedDateInfo.dayOfMonth) {
          task.repeatOnDay = parseInt(detectedDateInfo.dayOfMonth, 10);
          task._preserveRepeatOnDay = true;
          task.repeatOnLastDayOfMonth = false;
        } else {
          task.repeatOnDay = 1;
          task.repeatOnLastDayOfMonth = false;
        }
      }
      if (task.repeatFrequency === 'Yearly' && detectedDateInfo.dayOfMonth && detectedDateInfo.repeatOnMonth) {
        task.repeatOnDay = parseInt(detectedDateInfo.dayOfMonth, 10);
        task.repeatOnMonth = parseInt(detectedDateInfo.repeatOnMonth, 10);
      }
    }
  } else {
    const startOfToday = moment.tz(now, officeTimezone).startOf('day');
    task.dueAt = startOfToday.toDate();
    task.dueAtStr = startOfToday.format(dateFormat);
  }

  if (selectedDueDate) {
    const picked = moment.tz(selectedDueDate, officeTimezone).startOf('day');
    task.dueAt = picked.toDate();
    task.dueAtStr = picked.format(dateFormat);
  }

  const taggedPatients = getTaggedPatientIDs(content);
  if (taggedPatients.length > 0) task.taggedPatients = taggedPatients;

  const notifications = [];
  for (const assignee of assignees) {
    if (assignee !== userID) {
      notifications.push({
        audience: 'EMPLOYEE',
        employeeID: assignee,
        type: 'TASK',
        text: `You have been assigned a <a href='/app/todo/#taskID#'>task</a> by ${userName || 'a teammate'}`,
        siteID,
        createdAt: now,
        unreadList: [assignee],
      });
    }
  }

  return { task, notifications };
}
